import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const floatingWords = ['계열탐색', '진로발견', '전공선택', '직업탐구', '공부법찾기'];

export default function HeroSection({ hideAuthButton = false }: { hideAuthButton?: boolean }) {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [currentWord, setCurrentWord] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord(prev => (prev + 1) % floatingWords.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20high%20school%20students%20studying%20together%20in%20a%20modern%20bright%20library%20with%20books%20and%20laptops%2C%20warm%20soft%20natural%20light%20streaming%20through%20large%20windows%2C%20clean%20minimal%20educational%20space%2C%20students%20smiling%20and%20engaged%2C%20soft%20cream%20and%20sage%20green%20color%20palette&width=1600&height=700&seq=hero-edu-2026&orientation=landscape"
          alt="공부하는 학생들"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-900/60 to-primary-800/30"></div>
      </div>

      {/* Top-right auth button */}
      {!hideAuthButton && (
      <div className="absolute top-5 right-5 md:top-6 md:right-8 z-20">
        {session ? (
          <button
            onClick={() => navigate('/dashboard')}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white text-sm font-semibold px-4 py-2 rounded-md border border-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-dashboard-line"></i>
            대시보드
          </button>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white text-sm font-semibold px-4 py-2 rounded-md border border-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-login-box-line"></i>
            로그인
          </button>
        )}
      </div>
      )}

      {/* Content */}
      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-accent-500/20 border border-accent-400/40 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent-400 flex-shrink-0"></span>
            <span className="text-accent-200 text-sm font-medium">2022 개정 교육과정 완전 반영</span>
          </div>

          {/* Main Title */}
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
            나에게 맞는
            <br />
            <span className="text-accent-400 transition-all duration-500">
              {floatingWords[currentWord]}
            </span>
            ,
            <br />
            지금 시작하세요
          </h1>

          <p className="text-background-200 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
            5가지 도구로 우리 아이의 강점과 공부법을 정밀하게 진단하고,
            <br className="hidden md:block" />
            선택과목까지 이어서 맞춤 추천하는 종합 진단 플랫폼입니다.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/career-test')}
              className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors cursor-pointer text-sm md:text-base"
            >
              <i className="ri-compass-3-line mr-2"></i>
              진로 탐색 시작하기
            </button>
            <button
              onClick={() => navigate('/subject-guide')}
              className="whitespace-nowrap bg-white/15 hover:bg-white/25 text-white font-semibold px-7 py-3.5 rounded-lg border border-white/30 transition-colors cursor-pointer text-sm md:text-base"
            >
              <i className="ri-book-open-line mr-2"></i>
              선택과목 추천받기
            </button>
          </div>

          {/* Quick Stats */}
          <div className="flex flex-wrap gap-6 mt-10">
            {[
              { value: '5가지', label: '진로탐구 도구' },
              { value: '138개+', label: '선택 과목 안내' },
              { value: '6개', label: '계열별 맞춤 탐구' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-accent-400 font-bold text-xl md:text-2xl">{stat.value}</div>
                <div className="text-background-300 text-xs md:text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-white/50 text-xs">스크롤</span>
        <i className="ri-arrow-down-line text-white/50"></i>
      </div>
    </section>
  );
}